import { useEffect, useRef, useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import { getChartTheme, detectDarkMode, observeDarkMode } from '../chartTheme';

const COLORS = {
    ticker: '#F59E0B',
    industry: '#3B82F6',
    peers: '#60A5FA',
    peerItems: '#BFDBFE',
    referenceLine: '#F59E0B',
};

function formatValue(value, format) {
    if (value == null) return '-';
    if (format === 'percent') return `${value.toFixed(2)}%`;
    if (format === 'ratio') return `${value.toLocaleString('vi-VN', { maximumFractionDigits: 2 })}x`;
    return value.toLocaleString('vi-VN', { maximumFractionDigits: 2 });
}

function MetricChart({ metric, isDarkMode }) {
    const chartRef = useRef(null);

    useEffect(() => {
        if (!chartRef.current) return;

        const theme = getChartTheme(isDarkMode);
        const categories = [];
        const values = [];
        let tickerValue = null;
        let aggregateCount = 0;

        if (metric.ticker_value != null) {
            tickerValue = metric.ticker_value;
            categories.push(metric.ticker_label || 'Ticker');
            values.push({ y: metric.ticker_value, color: COLORS.ticker });
            aggregateCount++;
        }

        if (metric.industry_value != null) {
            categories.push('Ngành');
            values.push({ y: metric.industry_value, color: COLORS.industry });
            aggregateCount++;
        }

        if (metric.peers_avg != null) {
            categories.push('TB Peers');
            values.push({ y: metric.peers_avg, color: COLORS.peers });
            aggregateCount++;
        }

        (metric.peers || []).forEach((peer) => {
            categories.push(peer.ticker);
            values.push({ y: peer.value, color: COLORS.peerItems });
        });

        if (values.length === 0) return;

        const mutedColor = isDarkMode ? 'rgba(229,229,229,0.6)' : 'rgba(51,51,51,0.6)';
        const textColor = isDarkMode ? 'rgba(229,229,229,0.9)' : 'rgba(51,51,51,0.9)';
        const format = metric.format || 'ratio';

        const xAxisPlotBands = [];
        const xAxisPlotLines = [];

        if (aggregateCount > 0 && (metric.peers || []).length > 0) {
            xAxisPlotBands.push({
                from: -0.5,
                to: aggregateCount - 0.5,
                color: isDarkMode ? 'rgba(59,130,246,0.08)' : 'rgba(59,130,246,0.06)',
            });
            xAxisPlotLines.push({
                value: aggregateCount - 0.5,
                color: isDarkMode ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.15)',
                width: 1,
                dashStyle: 'Dash',
                zIndex: 5,
            });
        }

        Highcharts.chart(chartRef.current, {
            chart: {
                ...theme.chart,
                type: 'column',
                height: 220,
                marginBottom: 45,
            },
            title: { text: null },
            credits: theme.credits,
            legend: { enabled: false },
            xAxis: {
                ...theme.xAxis,
                categories,
                lineWidth: 0,
                tickLength: 0,
                gridLineWidth: 0,
                plotBands: xAxisPlotBands,
                plotLines: xAxisPlotLines,
                labels: {
                    ...theme.xAxis.labels,
                    rotation: -45,
                    style: { fontSize: '10px', color: mutedColor },
                    step: 1,
                    allowOverlap: false,
                },
            },
            yAxis: {
                ...theme.yAxis,
                title: { text: null },
                gridLineWidth: 0,
                labels: {
                    ...theme.yAxis.labels,
                    formatter: function () {
                        return formatValue(this.value, format);
                    },
                },
                plotLines: tickerValue != null ? [{
                    value: tickerValue,
                    color: COLORS.referenceLine,
                    dashStyle: 'Dash',
                    width: 1.5,
                    zIndex: 10,
                    label: {
                        text: formatValue(tickerValue, format),
                        align: 'right',
                        x: -5,
                        style: {
                            color: COLORS.referenceLine,
                            fontWeight: '600',
                            fontSize: '11px',
                        },
                    },
                }] : [],
            },
            tooltip: {
                ...theme.tooltip,
                useHTML: true,
                formatter: function () {
                    const categoryName = categories[this.point.index] || '';
                    return `<div style="padding:4px 2px">
                        <div style="font-weight:600;margin-bottom:6px;color:${textColor}">${categoryName}</div>
                        <div style="display:flex;align-items:center;gap:8px;font-size:12px">
                            <span style="color:${this.point.color}">●</span>
                            <span style="color:${mutedColor}">${metric.name}:</span>
                            <span style="font-weight:600;color:${textColor}">${formatValue(this.y, format)}</span>
                        </div>
                    </div>`;
                },
            },
            plotOptions: {
                column: {
                    ...theme.plotOptions?.column,
                    colorByPoint: true,
                    borderRadius: 2,
                    pointPadding: 0.1,
                    groupPadding: 0.15,
                    dataLabels: { enabled: false },
                },
            },
            series: [{ name: metric.name, data: values }],
        });
    }, [metric, isDarkMode]);

    return (
        <div>
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">{metric.name}</div>
            <div ref={chartRef} className="w-full" />
        </div>
    );
}

export default function PeerComparison({ ticker, metrics, title }) {
    const [isDarkMode, setIsDarkMode] = useState(detectDarkMode);

    useEffect(() => {
        return observeDarkMode(setIsDarkMode);
    }, []);

    const enrichedMetrics = useMemo(() =>
        (metrics || []).map(m => ({ ...m, ticker_label: m.ticker_label || ticker })),
        [metrics, ticker]
    );

    if (!enrichedMetrics || enrichedMetrics.length === 0) return null;

    const cols = enrichedMetrics.length > 1 ? 2 : 1;

    return (
        <div>
            {title && <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">{title}</div>}
            <div
                className="grid gap-4"
                style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
            >
                {enrichedMetrics.map((metric, i) => (
                    <MetricChart key={i} metric={metric} isDarkMode={isDarkMode} />
                ))}
            </div>
        </div>
    );
}
